import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Header from "../common/Header";
import FeasibilityList from "./FeasibilityList";
import StudyView from "./StudyView";
import InvitationViewer from "./InvitationViewer";
import QuestionTemplateManager from "./QuestionTemplateManager";
import NarrativeTemplateManager from "./NarrativeTemplateManager";
import { clearCurrentSFQ } from "./Redux/feasibility";
import { hasAnyPermission } from "../../utils/hasPermission";
import { PERM, SFQ_ACCESS } from "../../utils/permissions";

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 20px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-top: 20px;
`;

const PageTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #212529;
  margin: 0;
`;

const TabBar = styled.div`
  display: flex;
  gap: 2px;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  padding: 7px 18px;
  border: none;
  background: transparent;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 2px solid ${(p) => (p.$active ? "#0d6efd" : "transparent")};
  color: ${(p) => (p.$active ? "#0d6efd" : "#495057")};
  margin-bottom: -2px;
  &:hover { color: #0d6efd; }
`;

const DeniedBox = styled.div`
  margin-top: 60px;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
  line-height: 1.7;
`;

const DeniedTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  margin-top: 20px;
  margin-bottom: 16px;
  color: #495057;
  &:hover { background: #f8f9fa; }
`;

export default function Feasibility() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { sfqId: sfqIdParam, invId: invIdParam } = useParams();

  const selectedStudyId = sfqIdParam ? parseInt(sfqIdParam) : null;
  const selectedInvId   = invIdParam ? parseInt(invIdParam)  : null;

  const [tab, setTab] = useState("list"); // "list" | "narrative" | "questions"
  const [qtDirty, setQtDirty] = useState(false);

  // SFQ는 조회조차 권한이 필요하다. 세 레벨(목록/스터디/초대 응답)이 모두 이 컴포넌트를
  // 지나므로 진입은 여기 한 곳에서 막는다. API도 동일하게 403을 낸다.
  const permissions = useSelector((s) => s.userInfo.permissions);
  const canAccess   = hasAnyPermission(permissions, SFQ_ACCESS);
  // Authoring templates — including the org-wide "default" ones — is open to study
  // editors and SFQ admins alike; the API gates the writes with the same pair, so this
  // tab and the write permission are one and the same check.
  const canManageTemplates = hasAnyPermission(permissions, [PERM.SFQ_STUDY_EDIT, PERM.SFQ_ADMIN]);

  function handleTabChange(next) {
    if (qtDirty && tab === "questions" && next !== "questions") {
      if (!window.confirm("저장하지 않은 변경 사항이 있습니다.\n탭을 이동하면 변경 사항이 사라집니다.\n이동하시겠습니까?")) return;
    }
    setTab(next);
  }

  // Clear Redux state when leaving study detail
  useEffect(() => {
    if (!selectedStudyId) dispatch(clearCurrentSFQ());
  }, [selectedStudyId, dispatch]);

  function handleSelectStudy(id) {
    navigate(`/Feasibility/${id}`);
  }

  function handleBackToList() {
    navigate("/Feasibility");
  }

  function handleBackToStudy() {
    navigate(`/Feasibility/${selectedStudyId}`);
  }

  // 메뉴에서는 숨겨지지만 URL로는 들어올 수 있다. 헤더는 남겨 다른 메뉴로 나갈 수 있게 한다.
  if (!canAccess) {
    return (
      <Page>
        <Header />
        <Content>
          <DeniedBox>
            <DeniedTitle>접근 권한이 없습니다</DeniedTitle>
            Site Feasibility Questionnaire는 조회 권한이 있어야 열람할 수 있습니다.
            <br />
            권한이 필요하시면 관리자에게 문의해 주세요.
          </DeniedBox>
        </Content>
      </Page>
    );
  }

  // Level 3: Viewing a specific invitation response
  if (selectedStudyId && selectedInvId) {
    return (
      <Page>
        <Header />
        <Content>
          <BackBtn onClick={handleBackToStudy}>← Back to Study</BackBtn>
          <InvitationViewer
            sfqId={selectedStudyId}
            invId={selectedInvId}
            onClose={handleBackToStudy}
          />
        </Content>
      </Page>
    );
  }

  // Level 2: Study detail (narrative + questions + invitation list)
  if (selectedStudyId) {
    return (
      <Page>
        <Header />
        <Content>
          <BackBtn onClick={handleBackToList}>← Back to List</BackBtn>
          <StudyView
            sfqId={selectedStudyId}
            onSelectInvitation={(invId) => navigate(`/Feasibility/${selectedStudyId}/${invId}`)}
          />
        </Content>
      </Page>
    );
  }

  // Level 1: Study list + template management tabs
  return (
    <Page>
      <Header />
      <Content>
        <TopRow>
          <PageTitle>Site Feasibility Questionnaire</PageTitle>
        </TopRow>
        <TabBar>
          <Tab $active={tab === "list"} onClick={() => handleTabChange("list")}>SFQ List</Tab>
          {canManageTemplates && (
            <>
              <Tab $active={tab === "narrative"} onClick={() => handleTabChange("narrative")}>Narrative Template</Tab>
              <Tab $active={tab === "questions"} onClick={() => handleTabChange("questions")}>Question Template</Tab>
            </>
          )}
        </TabBar>
        {tab === "list"      && <FeasibilityList onSelect={handleSelectStudy} />}
        {canManageTemplates && tab === "narrative" && <NarrativeTemplateManager />}
        {canManageTemplates && tab === "questions" && <QuestionTemplateManager onDirtyChange={setQtDirty} />}
      </Content>
    </Page>
  );
}
